// PERF: this file is in the login entry graph. Import ONLY styles-only
// modules here — importing a component file welds its whole import graph
// (mapbox, the navigation menu, the storybook barrel…) to the entry chunk.
import { createGlobalStyle, css } from 'styled-components'

import { SubMenuStyle } from 'src/components/layout/sidebar/components/NavigationMenu/styles'
// FE1.1 + hauler-perf: styles-only modules on purpose - importing the components
// would drag the nav + mapbox graphs into the boot chunk.
import { MapboxGeocoderStyle } from 'src/mapbox/components/GeocoderControl.styles'
import { CssScroller } from 'src/storybook/components/CssScroller'
import { intercomMobileWidth } from 'src/styles/breakpoints'
import {
  bgInnerPrimary,
  bgPage,
  blue500,
  blue600,
  gray1000,
  gray200,
  gray300,
  gray400,
  gray500,
  gray600,
} from 'src/styles/color-theme'

import { INTERCOM_HANDSET_VERTICAL_PADDING } from './intercomPlacement'

const legacySass = css`
  html {
    height: 100%;
  }
  body {
    height: 100%;
    margin: 0;
    font-feature-settings: 'tnum', 'tnum';
    background-color: ${bgPage};
    font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
      Helvetica Neue, Arial, Noto Sans, sans-serif, Apple Color Emoji,
      Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
    font-size: 14px;
    font-variant: tabular-nums;
    line-height: 1.5715;
    #main {
      height: 100%;
      & .mainHolder {
        height: 100%;

        @media print {
          display: none;
        }
      }
    }
  }
  *,
  *::after,
  *::before {
    box-sizing: border-box;
  }
`

export const GlobalStyles = createGlobalStyle`
  ${MapboxGeocoderStyle}
  ${legacySass}
  ${SubMenuStyle}
  .ant-modal {
    &.variant-transparent {
      & .ant-modal-content {
        background-color: transparent !important;
        box-shadow: unset !important;
      }
    }
  }

  /* Both renderings of the src/antd/Tag seam: antd's .ant-tag with the
     design toggle off, shadcn's Badge with it on. */
  .ant-tag,
  [data-slot='badge'] {
    &.variant-round {
      border: none;
      background: ${gray300};
      border-radius: 16px;
    }
  }

  .wh-child-centered {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .wh-fill-parent {
    height: 100%;
    width: 100%;
     
  }
 
  /* The nine call sites that opt into these variants pass wh-antd-collapse to
   * src/antd's Collapse, which is a design-toggle seam — so every rule carries
   * a [data-slot='collapse-…'] twin beside its .ant-collapse-… one. The seam
   * re-emits none of antd's class names (see
   * src/antd/Collapse/Collapse.shadcn.tsx); these declarations are unlayered
   * and therefore outrank the seam's Tailwind utilities in both renderings,
   * which is what keeps an override here winning. */
  .wh-antd-collapse {
    &.header-full {
      .ant-collapse-header-text,
      [data-slot='collapse-header-text'] {
          width: 100%;
        }
    }

    &&&.expand-icon-top-12px {
      .ant-collapse-expand-icon span,
      [data-slot='collapse-expand-icon'] span {
        top: 12px;
      }
    }

    &&&.no-padding-content-box {
       .ant-collapse-content-box,
       [data-slot='collapse-content-box'] {
        padding: 0px;
      }
    }

    &&&.no-horizontal-padding {
      .ant-collapse-header,
      [data-slot='collapse-header'] {
        padding-left: 0;
        padding-right: 0;
      }

      .ant-collapse-expand-icon span,
      [data-slot='collapse-expand-icon'] span {
        right: 0;
      }

      .ant-collapse-content-box,
      [data-slot='collapse-content-box'] {
        padding-left: 0;
        padding-right: 0;
      }
    }

    &&&.no-vertical-padding {
      .ant-collapse-header,
      [data-slot='collapse-header'] {
        padding-top: 0;
        padding-bottom: 0;
      }

  
      .ant-collapse-content-box,
      [data-slot='collapse-content-box'] {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }

  /* Both renderings: src/antd/Card is antd's Card with the design toggle
     off and shadcn's with it on, and the shadcn half keys its parts by
     data-slot rather than re-emitting .ant-card-* (its header says why
     antd's names cannot be re-emitted while antd is still installed). The
     whf-ant-card / variant-* names are the APP's own className, which
     passes through unchanged in both modes. */
  .whf-ant-card {
    &.variant-secondary {
      &.ant-card,
      &[data-slot='card'] {
       border: none;
       .ant-card-body,
       [data-slot='card-body'] {
        border-radius: 4px;
        background: ${gray300};
       } 
     }
      &.variant-secondary-clickable {
        &.ant-card,
        &[data-slot='card'] {
          .ant-card-body,
          [data-slot='card-body'] {
             &:hover {
              background: ${gray400};
              cursor: pointer;
            }
          }
        } 
      }
    }
  }

  .whf-ant-timeline {
    &.no-last-item-padding {
      .ant-timeline-item-last {
        padding-bottom: 0px; 
      }
    }
    &.overwrite-min-height {
       .ant-timeline-item-content {
        min-height: 0px;
       }
    }
  }

  .mapboxgl-popup-content {
        padding: 0px;
  }

  .mapboxgl-popup {
    .mapboxgl-popup-content {
      box-shadow: 0px 3px 6px -4px #0000001f, 0px 6px 16px #00000014, 0px 9px 28px 8px #0000000d;
    }
  }
  
  // styling for MapV2 - doesn't interfere with MapV3
  .wh-mapbox-container .mapboxgl-popup .mapboxgl-popup-content {
    padding: 10px;
    width: fit-content;
  }
  
  #birdeatsbug-sdk {
    & :not(button, svg, path, input, textarea) {
      cursor: move;
      user-select: none;
    }

    &.draggable > div {
      cursor: grab;
    }

    & > div {
      background-color: #001529;
    }
    & button.btn-primary {
      background-color: ${blue600};
    }
  }

  /* Clear the side Intercom no longer uses — and nothing else.

     Bottom-LEFT (captain's call, 2026-09-11, restoring the corner it had
     before the 2026-08-25 move to bottom-right). The shipped antd shell takes
     that placement back exactly as it was, a fully scrolled nav column
     reaching the corner included — the widgets that claim a corner OUTRIGHT
     (Route Studio's Copilot FloatButton, the toast stack) are all
     bottom-RIGHT. Only the shadcn shell differs: components/main/shell fills
     its sidebar column with rows all the way down to the window's edge, so
     there the launcher stands beside that column rather than on it.

     The PRIMARY mechanism is the boot settings (src/App/useIntercom.ts):
     alignment: 'left' plus the two paddings from ./intercomPlacement. They are
     what move the launcher AND the messenger panel, the open/close animation
     origin and any ping bubbles, which CSS on the launcher alone cannot do —
     and they can be re-sent when the sidebar collapses or the design is
     switched, which a stylesheet cannot. So neither offset is restated HERE:
     an important left or bottom declaration would outrank them and freeze the
     launcher wherever it first landed. The one exception is deliberate and
     band-scoped — GlobalStylesIntercomHandsetLift below restates the BOTTOM
     inside Intercom's own mobile-width band (max-width: 450px), because
     Intercom discards the setting entirely at that size; its header carries
     the evidence and why freezing is the intent there.

     What is left is the backstop the 2026-08-25 rule existed for: reset the
     side Intercom no longer pins, so the launcher can never be silently
     ignored as an over-constrained box if its own stylesheet sets right
     again. */
  iframe.intercom-launcher-frame,
  .intercom-lightweight-app-launcher,
  #intercom-container .intercom-launcher,
  .intercom-app-launcher {
    right: auto !important;
  }

  .wh-antd-tabs-fill-height {
    .ant-tabs {
      height: 100%;
      .ant-tabs-content-holder {
        height: calc(100% - 46px);
        .ant-tabs-tabpane {
          height: 100%;
        }
      }
    }
  }

  .whf-antd-form {
    .hide-optional-mark {
      .ant-form-item-optional {
        display: none;
      }
    }

    &&& {
      /* The seam rendering gets a twin of every rule that reaches into antd's
         own class names — see the popup-seam note in AGENTS.md. antd's sheets
         are unlayered, so the seam re-emits none of them and keys its parts by
         data-slot instead. */
      .ant-select-disabled.wh-select-as-input-suffix {
        .ant-select-selector {
          border-top-color: ${gray500} !important;
          border-bottom-color: ${gray500} !important;
        }
      }

      [data-slot='select'][data-disabled].wh-select-as-input-suffix {
        [data-slot='select-selector'] {
          border-top-color: ${gray500} !important;
          border-bottom-color: ${gray500} !important;
        }
      }
    }
  }

  .whf-table-expandRow {
    &.bg-white {
      .ant-table-expanded-row-fixed {
        background-color: ${bgInnerPrimary};
      }
    }
    &.no-padding {
      .ant-table-expanded-row-fixed {
       padding: 0px;
      }
    }

    &.nochild-table-margin {
      .ant-table {
        margin: 0px !important;
      }
    }
  }

  .quick-select {
    & .ant-select-selector {
      border-color: transparent !important;
      padding-left: 4px !important;

      &:hover {
        background-color: #f8f8f8 !important;
      }
    }

    & .ant-select-selection-search {
      left: 4px !important
    }

    &.qs-8 {
      & .ant-select-selector {
        padding-left: 8px !important;
      }

      & .ant-select-selection-search {
        left: 8px !important
      }
    }

    &.ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input) .ant-select-selector {
      border-color: ${blue500} !important;
      background-color: ${gray200};
    }

    &.ant-select-single.ant-select-open .ant-select-selection-item {
      color: ${gray1000} !important;
    }

    &.ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
      background: transparent;
      color: ${gray1000};
    }

    &.ant-select-multiple.ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
      background: transparent;
    }

    /* The same six rules again for the seam's shadcn rendering. quick-select is
       a className on the control, which the seam puts on its own root, so every
       selector below is the antd one with .ant-select-* swapped for the
       matching data-slot. */
    & [data-slot='select-selector'] {
      border-color: transparent !important;
      padding-left: 4px !important;

      &:hover {
        background-color: #f8f8f8 !important;
      }
    }

    & [data-slot='select-selection-search'] {
      left: 4px !important;
    }

    &.qs-8 {
      & [data-slot='select-selector'] {
        padding-left: 8px !important;
      }

      & [data-slot='select-selection-search'] {
        left: 8px !important;
      }
    }

    &[data-slot='select'][data-open]:not([data-disabled])
      [data-slot='select-selector'] {
      border-color: ${blue500} !important;
      background-color: ${gray200};
    }

    &[data-slot='select'][data-open]:not([data-mode])
      [data-slot='select-selection-item'] {
      color: ${gray1000} !important;
    }

    &[data-slot='select'][data-disabled] [data-slot='select-selector'] {
      background: transparent;
      color: ${gray1000};
    }

    &[data-slot='select'][data-disabled][data-mode='multiple']
      [data-slot='select-selector'] {
      background: transparent;
    }
  }
  
  .wh-tag-light {
    background: ${gray300};
    border-radius: 4px;
    &.size-small {
      padding: 4px 10px;
    }
  }
  
  &.ant-btn-text.variant-text--underlined {
    &:disabled {
      > span {
        color: ${gray600};
      }
    }

    &:not(.ant-btn-text[disabled]) {
      &:hover {
        background: none;
         > span {
           text-decoration: underline;
         }
      }
      
      &:focus {
        background: none;
      }
    }
  }
  
  .whf-center-child { 
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .whf-ant-popover {
    &&& {
      &.no-arrow {
        padding: 0 !important;
      }
      &.no-content-padding {
        .ant-popover-inner-content {
          padding: 0px
        }
      }
    }
    .ant-popover-inner {
      border-radius: 4px;
      overflow: hidden;
    }
  }

  /* Both renderings: src/antd/Input is antd's Input with the design toggle
     off and shadcn's with it on, and the shadcn half keys its parts by
     data-slot rather than re-emitting .ant-input-*. wh-ant-input and
     variant--soft are the APP's own className, which passes through
     unchanged in both modes. */
  .wh-ant-input {
    &.variant--soft {
      /* for input without any wrappers */
      &.ant-input, &.ant-input-affix-wrapper,
      &[data-slot='input'], &[data-slot='input-affix-wrapper'] {
          border:none;
          background: ${gray300};
          box-shadow: none;
      } 

      /* When input has suffix/affix */
      .ant-input-group-addon,
      [data-slot='input-addon'] {
        border: none;
        background: ${gray300};
      }
      /* When input has suffix/affix */
      .ant-input-affix-wrapper, .ant-input,
      [data-slot='input-affix-wrapper'], [data-slot='input'] {
        border:none;
        background: ${gray300};
        box-shadow: none;
        padding-left: 0;
      } 
    }
  }

  .fill-view {
    width: 100vw;
    height: 100vh;
  }

  .fill-parent {
    width: 100%;
    height: 100%;
  }
  
  .fill-parent-height {
    height: 100%;
  }
  
  .fill-parent-width {
    width: 100%;
  }

  .center-child {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .center-child-horizontal {
    display: flex;
    justify-content: center;
  }
  
  .center-child-vertical {
    display: flex;
    align-items: center;
  }

  .flex {
    display: flex;
    ${[
      ['xs', `0.25rem`],
      ['sm', '0.5rem'],
      ['md', '0.75rem'],
      ['lg', '1rem'],
    ].map(
      ([sizeName, sizeValue]) => `
       &.flex-rg-${sizeName} {
         row-gap: ${sizeValue};
       }
        
       &.flex-cg-${sizeName} {
         column-gap: ${sizeValue};
       }
      `
    )}

    &.flex-column {
      flex-direction: column;
      & > .flex-column--full-child {
        flex: 1;
        min-height: 0;
      }
    }
  }

  ${[
    ['xs', `0.25rem`],
    ['sm', '0.5rem'],
    ['md', '0.75rem'],
    ['lg', '1rem'],
  ].map(
    ([sizeName, sizeValue]) => `
      .pd-${sizeName} {
        padding: ${sizeValue};
      }
    `
  )}

  .wh-antd-form-item {
    &.no-label {
      .ant-form-item-label {
        display: none;
      }
    }
  }
  
  .table-row-dragging {
    display: flex;
    justify-content: space-between;
    background-color: ${gray200};
    td {
      padding: 8px 16px;
      flex: 1;
      background-color: ${gray200};
    }
  }

  .ant-dropdown-menu,
  .ant-popover-inner,
  .ant-modal-content,
  .ant-select-dropdown,
  [data-slot='dropdown-menu-content'],
  [data-slot='select-dropdown'] {
    border-radius: 4px;
  }

  .ant-modal-header {
    border-radius: 4px 4px 0 0;
  }

  /* PRODUCT-154 — dark toast skin. react-toastify here is v6 (no \`theme\` prop),
     so the dark variant is a scoped CSS override, not a library upgrade. The
     container portals to <body>, which sits under <html data-theme='dark'>, so
     this selector reaches it; light theme keeps the library default. */
  [data-theme='dark'] {
    .Toastify__toast {
      background: var(--wh-gray-200);
      color: var(--wh-text-primary);
      border: 1px solid var(--wh-b-section);
    }
    .Toastify__toast-body {
      color: var(--wh-text-primary);
    }
    .Toastify__close-button {
      color: var(--wh-text-secondary);
      opacity: 0.85;
    }
    .Toastify__toast--success .Toastify__progress-bar {
      background: var(--wh-green-500);
    }
    .Toastify__toast--error .Toastify__progress-bar {
      background: var(--wh-red-500);
    }
    .Toastify__toast--warning .Toastify__progress-bar {
      background: var(--wh-gold-500);
    }
    .Toastify__toast--info .Toastify__progress-bar,
    .Toastify__toast--default .Toastify__progress-bar {
      background: var(--wh-blue-500);
    }
  }
`

/**
 * PRO-929 — the handset lift, AS CSS, because Intercom throws the boot
 * setting away on a phone.
 *
 * `intercomPlacement`'s `INTERCOM_HANDSET_VERTICAL_PADDING` has never reached
 * a 360px screen, and nothing in this repo could have told you so: the
 * setting is sent, `window.intercomSettings` carries it, and Intercom
 * silently ignores it. From Intercom's own shipped widget
 * (`js.intercomcdn.com/frame-modern.*.js`, read 2026-10-07), two places:
 *
 *   isMobile = innerWidth <= 450
 *           || (innerWidth <= 900 && innerHeight <= 450)
 *
 *   // the launcher's own rule
 *   bottom: isMobile ? 20 : Math.max(verticalPadding || 20, 20)
 *   // and the customization reducer, which discards BOTH paddings first
 *   verticalPaddingPx = (isMobile && !isHeightSetByBanner) ? 20 : …
 *
 * So at 360 and 412 the launcher is pinned 20px off the bottom edge whatever
 * we send, which is exactly what the J6.8 re-test saw. THE BAND IS INTERCOM'S
 * OWN WIDTH TEST (`intercomMobileWidth`, max-width: 450px), not this app's
 * handset band: from 451px up the boot setting is honoured and already lifts
 * the launcher, so a wider rule would be a second `!important` copy of a
 * placement the boot settings own — the freeze `GlobalStyles` warns about
 * above.
 *
 * ACCEPTED NARROWING, so the next reader does not "fix" it. Intercom's full
 * test has a second clause — `innerWidth <= 900 && innerHeight <= 450` — so a
 * phone in LANDSCAPE (700x400, 844x390) is mobile to Intercom and has its
 * padding discarded, while this width-only rule does not reach it and the
 * launcher stays at Intercom's own 20px there. That is a known, accepted
 * limit, not an oversight: a height-conditioned rule would also claim short
 * DESKTOP windows, where the boot setting works and must keep owning
 * placement.
 *
 * `!important` is required and is NOT the freeze `GlobalStyles` warns about:
 * the real launcher is an iframe Intercom positions with an INLINE `bottom`,
 * which nothing weaker outranks, and the lightweight one is styled from a
 * stylesheet Intercom injects at runtime (so after this one). The freeze that
 * warning is about would matter if the value moved, and inside this band it
 * does not — `INTERCOM_HANDSET_VERTICAL_PADDING` is a constant and the rule
 * stops applying at the band's edge, where the boot settings take the
 * launcher back.
 *
 * Only the BOTTOM is restated. Intercom pins the aligned side to 20px on a
 * phone too, against this app's 15px; the captain's ruling is about the
 * CORNER, and bottom-left is what both values give. The messenger PANEL needs
 * nothing here either — Intercom renders it full-screen on a phone, so there
 * is no offset left for a stylesheet to get wrong.
 *
 * Mounted by `components/main` on the ANTD branch only, for the reason
 * `intercomPlacement` gives: the shadcn shell's bottom-left is rows all the
 * way down, so it moves the launcher sideways instead and a lift there would
 * trade one nav row for the next.
 */
export const GlobalStylesIntercomHandsetLift = createGlobalStyle`
  ${intercomMobileWidth} {
    iframe.intercom-launcher-frame,
    .intercom-lightweight-app-launcher,
    #intercom-container .intercom-launcher,
    .intercom-app-launcher {
      bottom: ${INTERCOM_HANDSET_VERTICAL_PADDING}px !important;
    }
  }
`

export const GlobalStylesAntFillViewList = createGlobalStyle`
  .wh-ant-fill-view-list {
    display: flex;
    flex-direction: column;
    height: 100%;

    .ant-spin-nested-loading {
      flex: 1;
      min-height: 0;
      ${CssScroller}
    }
  }
`
